import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ServiceRequestPaymentStatus } from "@gloria-public/order/domain/interfaces/cart.interface";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { IRatio, IResultSerializable, IResultStatus, IServiceRequestTemplateResponse } from "@shared/interfaces/service-requests.interface";
import { ServiceRequestService } from "@shared/services/service-requests.service";
import { Subject, finalize, first } from "rxjs";

type ResultVM = IResultSerializable & {
  ratioPercent?: number;
  ticks?: { active: boolean; height: number }[];
};

@Component({
  selector: "app-service-request-dialog",
  templateUrl: "./service-request-dialog.component.html",
  styleUrls: ["./service-request-dialog.component.scss"],
})
export class ServiceRequestDialogComponent implements OnInit, OnDestroy {
  @Output() allResultsSeen = new EventEmitter<void>();
  private allSeenEmitted = false;

  public results: ResultVM[] = [];
  public currentSlideIndex = 0;
  public loadingAddToCart = false;
  public loadingUnsubscribe = false;
  private readonly seenOrPendingResults = new Set<string>();

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private orderService: OrderService,
    private serviceRequestService: ServiceRequestService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<ServiceRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      response: IServiceRequestTemplateResponse;
      results?: IResultSerializable[];
      riskId: string;
      canPurchase: boolean;
      alreadyBought: boolean;
      paymentStatus?: ServiceRequestPaymentStatus;
      serviceRequestId?: number;
      paidUntil?: string;
    }
  ) {}

  /**
   * Computes the ratioPercent and ticks for each results
   */
  ngOnInit(): void {
    this.results =
      this.data.results?.map((r) => ({
        ...r,
        ratioPercent: r.valueRatio ? this.getRatioPercent(r.valueRatio) : undefined,
        ticks: r.valueInteger !== undefined && r.valueInteger !== null ? this.getTicks(r.valueInteger) : undefined,
      })) ?? [];
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public onResultViewed(result: ResultVM): void {
    const id = result.identifier.value;

    if (result.status !== IResultStatus.NOT_READ || this.seenOrPendingResults.has(id)) {
      return;
    }

    this.seenOrPendingResults.add(id);

    this.serviceRequestService
      .markResultAsSeen(id)
      .pipe(first())
      .subscribe({
        next: () => {
          setTimeout(() => {
            result.status = IResultStatus.READ;
            const original = this.data.results?.find((r) => r.identifier.value === id);
            if (original) {
              original.status = IResultStatus.READ;
            }
            if (!this.allSeenEmitted && this.data.results?.every((r) => r.status === IResultStatus.READ)) {
              this.allSeenEmitted = true;
              this.allResultsSeen.emit();
            }
          }, 3000);
        },
        error: () => {
          this.seenOrPendingResults.delete(id);
        },
      });
  }

  private getRatioPercent(valueRatio: IRatio): number {
    const { numerator, denominator } = valueRatio;
    return denominator.value ? (numerator.value / denominator.value) * 100 : 0;
  }

  private getTicks(valueInteger: number): { active: boolean; height: number }[] {
    const max = 10;
    const val = valueInteger ?? 0;
    return Array.from({ length: max }, (_, i) => ({
      active: i < val,
      height: 40 + (i / max) * 60,
    }));
  }

  /**
   * Closes dialog and returns to previous view
   */
  public close(): void {
    this.dialogRef.close({
      paymentStatus: this.data.paymentStatus,
    });
  }

  /**
   * Gets service request IDs currently in cart
   * @return Set of service request IDs
   */
  public get serviceRequestIDS(): Set<string> {
    return this.orderService.serviceRequestIDs;
  }

  /**
   * Checks whether a service request is already in cart
   * @param serviceRequestId Service request identifier
   * @return True if item exists in cart, otherwise false
   */
  public isInCart(serviceRequestId: string): boolean {
    return this.serviceRequestIDS.has(serviceRequestId);
  }

  /**
   * Handles cart button click event
   * @param event Mouse event
   */
  public onCartClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggleCart();
  }

  /**
   * Toggles service request in cart (add or remove)
   */
  public toggleCart(): void {
    const serviceRequestId = this.data.response.identifier.value;

    if (this.loadingAddToCart) return;

    this.loadingAddToCart = true;

    const isAdd = !this.isInCart(serviceRequestId);

    const request$ = isAdd
      ? this.orderService.addToCartFlow(
          {
            serviceRequestID: serviceRequestId,
            riskTemplateID: this.data.riskId,
          },
          this.data.response.title
        )
      : this.orderService.removeFromCartFlow(serviceRequestId);

    request$.pipe(finalize(() => (this.loadingAddToCart = false))).subscribe({});
  }

  /**
   * Handles unsubscribe button click event
   * @param event Mouse event
   */
  public unsubscribe(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.data.serviceRequestId) {
      return;
    }
    this.loadingUnsubscribe = true;

    this.orderService
      .cancelSubscription(this.data.serviceRequestId)
      .pipe(
        first(),
        finalize(() => (this.loadingUnsubscribe = false))
      )
      .subscribe({
        next: () => {
          this.data.paymentStatus = ServiceRequestPaymentStatus.CANCELED;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        },
      });
  }
}
